import React, { FC } from 'react'

import { FieldErrors, UseFormRegister, UseFormWatch } from 'react-hook-form'
import { UseFormSetValue } from 'react-hook-form/dist/types/form'

import { ErrorTxt } from 'src/components/atoms'
import { TextInput } from 'src/components/atoms/inputs/TextInput'
import {
  ProjectCodeTooltipContent,
  ProjectStatusDropdown,
  ProjectStatusTooltipContent,
} from 'src/components/molecules'
import { FormItemVertical } from 'src/components/molecules/formSets/FormItemVertical'
import { ProjectEmailDisplayNameInputArea } from 'src/components/molecules/projects/ProjectEmailDisplayNameInputArea'
import { ProjectEmailTooltipContent } from 'src/components/molecules/projects/ProjectEmailTooltipContent'

import { ProjectStatusType, UpsertProjectFormEntity } from 'src/types'
import { EmailDomainEntity } from 'src/types/entities/EmailDomain'

type Props = {
  projectStatuses: ProjectStatusType[]
  currentStatus?: ProjectStatusType
  emailDomains: EmailDomainEntity[]
  register: UseFormRegister<UpsertProjectFormEntity>
  watch: UseFormWatch<UpsertProjectFormEntity>
  setValue: UseFormSetValue<UpsertProjectFormEntity>
  formErrors: FieldErrors<UpsertProjectFormEntity>
  className?: string
}

export const ProjectBasicInfoInputArea: FC<Props> = ({
  projectStatuses,
  currentStatus,
  emailDomains,
  register,
  watch,
  setValue,
  formErrors,
  className,
}) => {
  const isEmailDomainEnabled = emailDomains.length > 0
  const watchedEmail = watch('email')
  const watchedEmailDisplayName = watch('emailDisplayName')

  // 送信元メールアドレスが入力出来て、送信元メールアドレスを1文字でも入力した後、送信元名を表示する。もしくは 送信元名 が既に入力されている場合は表示する
  const isShowEmailDisplayNameInput =
    (isEmailDomainEnabled && watchedEmail && watchedEmail.length > 0) || !!watchedEmailDisplayName

  return (
    <div className={className}>
      <div className="tw-grid tw-gap-s2">
        <FormItemVertical title="プロジェクト名称" showRequired>
          <TextInput
            placeholder="プロジェクト名"
            {...register('name')}
            hasError={!!formErrors.name}
            className="tw-w-full"
          />
          {formErrors.name && (
            <ErrorTxt text={formErrors.name.message ?? ''} showIcon iconPlacement="left" />
          )}
        </FormItemVertical>
        <FormItemVertical title="管理コード" tooltipContent={<ProjectCodeTooltipContent />}>
          <TextInput
            placeholder="社内管理コード"
            {...register('code')}
            hasError={!!formErrors.code}
            className="tw-w-full"
          />
          {formErrors.code && (
            <ErrorTxt text={formErrors.code.message ?? ''} showIcon iconPlacement="left" />
          )}
        </FormItemVertical>
        <FormItemVertical title="ステータス" tooltipContent={<ProjectStatusTooltipContent />}>
          <ProjectStatusDropdown
            projectStatuses={projectStatuses}
            currentStatus={currentStatus}
            value={watch('statusName')}
            selectOption={option => setValue('statusName', option)}
            className="tw-w-1/3"
          />
          {formErrors.statusName && (
            <ErrorTxt text={formErrors.statusName.message ?? ''} showIcon iconPlacement="left" />
          )}
        </FormItemVertical>
        <div className="tw-grid tw-gap-s-4">
          <FormItemVertical
            title="送信元メールアドレス"
            tooltipContent={<ProjectEmailTooltipContent />}
          >
            <TextInput
              placeholder="example@styleport.co.jp"
              disabled={!isEmailDomainEnabled}
              {...register('email')}
              hasError={!!formErrors.email}
              className="tw-w-full"
            />
            {formErrors.email && (
              <ErrorTxt text={formErrors.email.message ?? ''} showIcon iconPlacement="left" />
            )}
          </FormItemVertical>

          {isShowEmailDisplayNameInput && (
            <ProjectEmailDisplayNameInputArea
              register={register}
              watch={watch}
              setValue={setValue}
              formErrors={formErrors}
            />
          )}
        </div>
      </div>
    </div>
  )
}
